import React from 'react';
import { Button } from './ui/button';
import { MapPin, Users, Trophy, Clock, Phone, Mail } from 'lucide-react';

const MatchdayCatering = () => {
  const clubs = [
    {
      name: 'Chadderton FC',
      description: 'Providing matchday catering services for players, staff, and supporters'
    },
    {
      name: 'Mancunian Unity FC',
      description: 'Hot food and refreshments on every matchday'
    }
  ];

  const services = [
    {
      icon: Users,
      title: 'Food Stand Services',
      description: 'Professional food stand setup providing hot food and drinks to all visitors, supporters, and team members on matchday.'
    },
    {
      icon: Trophy,
      title: 'Pre & Post-Match Catering',
      description: 'Nutritious meals for teams before games and celebratory refreshments after the final whistle.'
    },
    {
      icon: Clock,
      title: 'Reliable Service',
      description: 'We understand matchday timings are crucial. Our team is always ready, rain or shine, for every home game.'
    }
  ];

  const offerings = [
    'Hot dogs & burgers',
    'Hot drinks (tea, coffee, hot chocolate)',
    'Cold drinks & refreshments',
    'Chips & snacks',
    'Pre-match team meals',
    'Post-match buffets',
    'Custom catering packages',
    'Event day specials'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20" style={{ backgroundColor: 'var(--color-accent-coconut)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: 'var(--text-base)' }}>
              Matchday Catering Services
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto" style={{ color: 'var(--text-subtle-light)' }}>
              Professional food stand catering for football clubs and sports venues across Greater Manchester
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_375a34ef-dc0c-49f6-a0f6-822b7ef71c43/artifacts/9o0mt69f_360648de-99ad-456c-b075-bad0842ff937.jpeg"
                alt="Proud Catering Partners of Chadderton FC"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl font-bold" style={{ color: 'var(--text-base)' }}>
                Fueling Your Matchday Experience
              </h2>
              <p className="text-lg" style={{ color: 'var(--text-subtle-light)' }}>
                At Marlowes Catering, we provide comprehensive matchday catering services through our professional food stand setup. From hot food and drinks for supporters to pre and post-match meals for teams, we ensure everyone is well-fed and ready for the game.
              </p>
              <p className="text-lg" style={{ color: 'var(--text-subtle-light)' }}>
                With our 5-star food hygiene rating and years of experience, you can trust us to deliver quality food service that matches the excitement of matchday.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: 'var(--text-base)' }}>
              What We Offer
            </h2>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: 'var(--text-subtle-light)' }}>
              Complete matchday catering solutions for clubs of all sizes
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="p-8 rounded-xl transition-all card-hover"
                  style={{ backgroundColor: 'var(--color-accent-coconut)' }}
                >
                  <div className="mb-4">
                    <div
                      className="w-16 h-16 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: 'white' }}
                    >
                      <Icon size={32} style={{ color: 'var(--accent-ui-accent)' }} />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-base)' }}>
                    {service.title}
                  </h3>
                  <p style={{ color: 'var(--text-subtle-light)' }}>
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Menu Items Grid */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h3 className="text-3xl font-bold mb-8 text-center" style={{ color: 'var(--text-base)' }}>
              Matchday Menu
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {offerings.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 p-4 rounded-lg"
                  style={{ backgroundColor: 'var(--color-accent-coconut)' }}
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: 'var(--accent-ui-accent)' }}
                  ></span>
                  <span className="font-medium" style={{ color: 'var(--text-base)' }}>
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Current Clubs Section */}
      <section className="py-20" style={{ backgroundColor: 'var(--color-accent-coconut)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: 'var(--text-base)' }}>
              Clubs We Serve
            </h2>
            <p className="text-xl" style={{ color: 'var(--text-subtle-light)' }}>
              Proudly providing matchday catering for local football clubs
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {clubs.map((club, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg text-center">
                <Trophy size={48} className="mx-auto mb-4" style={{ color: 'var(--accent-ui-accent)' }} />
                <h3 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-base)' }}>
                  {club.name}
                </h3>
                <p style={{ color: 'var(--text-subtle-light)' }}>
                  {club.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: 'var(--text-base)' }}>
              Matchday in Action
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <img
              src="https://customer-assets.emergentagent.com/job_375a34ef-dc0c-49f6-a0f6-822b7ef71c43/artifacts/9o0mt69f_360648de-99ad-456c-b075-bad0842ff937.jpeg"
              alt="Chadderton FC team - Proud Catering Partners"
              className="rounded-xl shadow-lg w-full h-64 object-cover"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_375a34ef-dc0c-49f6-a0f6-822b7ef71c43/artifacts/k03h7pm9_IMG_4512.jpeg"
              alt="Delicious chicken wrap with fries"
              className="rounded-xl shadow-lg w-full h-64 object-cover"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_375a34ef-dc0c-49f6-a0f6-822b7ef71c43/artifacts/pg83v1ka_dbb1d8ed-5552-4426-b7b3-364747e25d79.jpeg"
              alt="Fresh burger platter with sides"
              className="rounded-xl shadow-lg w-full h-64 object-cover"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_375a34ef-dc0c-49f6-a0f6-822b7ef71c43/artifacts/9k77itgn_IMG_4378.jpeg"
              alt="Breakfast burger with coffee"
              className="rounded-xl shadow-lg w-full h-64 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Expansion CTA Section */}
      <section className="py-20" style={{ backgroundColor: 'var(--surface-background-dark)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Looking for Matchday Catering?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            We're actively seeking opportunities to expand our matchday catering services to other sports clubs across Greater Manchester and beyond. Whether you're a football club, rugby team, or any sports organization, we'd love to discuss how we can enhance your matchday experience.
          </p>
          
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-white mb-4">
              Why Choose Marlowes for Your Club?
            </h3>
            <ul className="text-left text-white/90 space-y-3 max-w-2xl mx-auto">
              <li className="flex items-start">
                <span className="text-lime-400 mr-3">✓</span>
                <span>5-star food hygiene rating for your peace of mind</span>
              </li>
              <li className="flex items-start">
                <span className="text-lime-400 mr-3">✓</span>
                <span>Reliable service - we're there every matchday, rain or shine</span>
              </li>
              <li className="flex items-start">
                <span className="text-lime-400 mr-3">✓</span>
                <span>Flexible packages tailored to your club's needs</span>
              </li>
              <li className="flex items-start">
                <span className="text-lime-400 mr-3">✓</span>
                <span>Competitive pricing with quality you can trust</span>
              </li>
              <li className="flex items-start">
                <span className="text-lime-400 mr-3">✓</span>
                <span>Professional food stand setup and experienced staff</span>
              </li>
            </ul>
          </div>

          <div className="space-y-4">
            <Button
              size="lg"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="text-xl px-12 py-8"
              style={{ backgroundColor: 'var(--color-accent-lime)', color: 'var(--text-base)' }}
            >
              Contact Us About Matchday Catering
            </Button>
            <p className="text-white/80">
              Or call us directly on <a href="tel:01612228831" className="text-lime-400 font-bold hover:underline">0161 222 8831</a>
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <Phone size={32} className="mx-auto mb-3" style={{ color: 'var(--accent-ui-accent)' }} />
              <h4 className="font-bold mb-2" style={{ color: 'var(--text-base)' }}>Phone</h4>
              <a href="tel:01612228831" className="hover:opacity-70" style={{ color: 'var(--text-subtle-light)' }}>
                0161 222 8831
              </a>
            </div>
            <div>
              <Mail size={32} className="mx-auto mb-3" style={{ color: 'var(--accent-ui-accent)' }} />
              <h4 className="font-bold mb-2" style={{ color: 'var(--text-base)' }}>Email</h4>
              <a href="mailto:info@marlowescatering.co.uk" className="hover:opacity-70" style={{ color: 'var(--text-subtle-light)' }}>
                info@marlowescatering.co.uk
              </a>
            </div>
            <div>
              <MapPin size={32} className="mx-auto mb-3" style={{ color: 'var(--accent-ui-accent)' }} />
              <h4 className="font-bold mb-2" style={{ color: 'var(--text-base)' }}>Location</h4>
              <p style={{ color: 'var(--text-subtle-light)' }}>
                Chadderton, Oldham<br />Greater Manchester
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MatchdayCatering;
